/**
 * Meta Pixel + Conversion API Integration
 * 
 * Features:
 * - GDPR compliant (only tracks after marketing consent)
 * - Event deduplication with event_id
 * - Both client-side Pixel and server-side Conversion API
 * - Supports all standard Meta events
 * - Debug logging for troubleshooting
 * - Global event log for /meta-debug page
 */

// Get Pixel ID from environment
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1289244013192694';

// Debug mode - set to true to see all logs
const DEBUG = true;

// Global event log for debugging (accessible from /meta-debug)
if (typeof window !== 'undefined') {
  window.__metaPixelEventLog = window.__metaPixelEventLog || [];
}

const logEvent = (eventName, eventData, eventId, status, reason = null) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    eventName,
    eventData,
    eventId,
    status, // 'sent', 'blocked', 'error'
    reason,
    url: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
  };
  
  if (typeof window !== 'undefined') {
    window.__metaPixelEventLog = window.__metaPixelEventLog || [];
    window.__metaPixelEventLog.push(logEntry);
    // Keep only last 100 events
    if (window.__metaPixelEventLog.length > 100) {
      window.__metaPixelEventLog = window.__metaPixelEventLog.slice(-100);
    }
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('metaPixelEvent', { detail: logEntry }));
  }
  
  if (DEBUG) {
    const statusIcon = status === 'sent' ? '✅' : status === 'blocked' ? '🚫' : '❌';
    console.log(`[MetaPixel] ${statusIcon} ${eventName}`, { eventId, status, reason, eventData });
  }
};

const log = (...args) => {
  if (DEBUG) {
    console.log('[MetaPixel]', ...args);
  }
};

// Generate unique event ID for deduplication
export const generateEventId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Check if marketing consent is given
export const hasMarketingConsent = () => {
  console.log('[META DEBUG] hasMarketingConsent() called');
  try {
    // Method 1: Check window flag (most reliable, set by CookieBanner)
    if (typeof window !== 'undefined' && window.__metaPixelConsent === true) {
      log('Consent found via window.__metaPixelConsent');
      console.log('[META DEBUG] ✅ Consent found via window.__metaPixelConsent');
      return true;
    }
    
    // Method 2: Check localStorage
    const consent = localStorage.getItem('smartballoon-cookie-consent');
    console.log('[META DEBUG] localStorage consent:', consent);
    if (consent) {
      const parsed = JSON.parse(consent);
      console.log('[META DEBUG] Parsed localStorage consent:', parsed);
      if (parsed.marketing === true) {
        log('Consent found via localStorage');
        console.log('[META DEBUG] ✅ Consent found via localStorage (marketing=true)');
        // Also set window flag for faster future checks
        if (typeof window !== 'undefined') {
          window.__metaPixelConsent = true;
        }
        return true;
      }
    }
    
    // Method 3: Check sessionStorage
    const sessionConsent = sessionStorage.getItem('smartballoon-cookie-consent');
    console.log('[META DEBUG] sessionStorage consent:', sessionConsent);
    if (sessionConsent) {
      const parsed = JSON.parse(sessionConsent);
      if (parsed.marketing === true) {
        log('Consent found via sessionStorage');
        console.log('[META DEBUG] ✅ Consent found via sessionStorage');
        if (typeof window !== 'undefined') {
          window.__metaPixelConsent = true;
        }
        return true;
      }
    }
    
    // Method 4: Check window.__cookieConsent object
    if (typeof window !== 'undefined' && window.__cookieConsent) {
      const parsed = typeof window.__cookieConsent === 'string' 
        ? JSON.parse(window.__cookieConsent) 
        : window.__cookieConsent;
      if (parsed.marketing === true) {
        log('Consent found via window.__cookieConsent');
        console.log('[META DEBUG] ✅ Consent found via window.__cookieConsent');
        window.__metaPixelConsent = true;
        return true;
      }
    }
    
    console.log('[META DEBUG] ❌ No marketing consent found in any storage');
    log('No marketing consent found');
  } catch (e) {
    console.log('[META DEBUG] ❌ Error reading consent:', e);
    log('Error reading consent:', e);
  }
  return false;
};

// Initialize Meta Pixel (only if consent given)
export const initMetaPixel = () => {
  log('initMetaPixel called, Pixel ID:', META_PIXEL_ID);
  
  if (!META_PIXEL_ID) {
    log('ERROR: No Pixel ID configured');
    return false;
  }
  
  if (!hasMarketingConsent()) {
    log('Marketing consent not given, skipping initialization');
    return false;
  }
  
  // Check if already initialized
  if (typeof window !== 'undefined' && window.fbq && window.__fbqInitialized) {
    log('Already initialized');
    return true;
  }
  
  log('Initializing Meta Pixel...');
  
  // Initialize fbq
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) {
      log('fbq already exists, reinitializing');
      f.fbq('init', META_PIXEL_ID);
      f.__fbqInitialized = true;
      return;
    }
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    t.onload = function() {
      log('Facebook SDK loaded successfully');
    };
    t.onerror = function(error) {
      log('ERROR loading Facebook SDK:', error);
    };
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', META_PIXEL_ID);
  window.__fbqInitialized = true;
  
  log('Meta Pixel initialized with ID:', META_PIXEL_ID);
  
  return true;
};

// Force initialize pixel (bypasses consent check - use only for testing)
export const forceInitMetaPixel = () => {
  log('FORCE initializing Meta Pixel (bypassing consent)');
  
  if (typeof window !== 'undefined') {
    window.__metaPixelConsent = true;
  }
  
  return initMetaPixel();
};

// Track PageView event
export const trackPageView = (url) => {
  const pagePath = url || (typeof window !== 'undefined' ? window.location.pathname : 'unknown');
  log('trackPageView called for:', pagePath);
  
  if (!hasMarketingConsent()) {
    logEvent('PageView', { url: pagePath }, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  if (!initMetaPixel()) {
    logEvent('PageView', { url: pagePath }, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId();
    window.fbq('track', 'PageView', {}, { eventID: eventId });
    logEvent('PageView', { url: pagePath }, eventId, 'sent');
    return eventId;
  }
  
  logEvent('PageView', { url: pagePath }, null, 'error', 'fbq not available');
  return null;
};

// Track CompleteRegistration event
export const trackCompleteRegistration = (userData = {}) => {
  log('trackCompleteRegistration called');
  
  const eventData = {
    content_name: 'SmartBalloon Registration',
    status: 'completed',
    ...userData
  };
  
  if (!hasMarketingConsent()) {
    logEvent('CompleteRegistration', eventData, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  if (!initMetaPixel()) {
    logEvent('CompleteRegistration', eventData, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  const eventId = generateEventId();
  
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'CompleteRegistration', eventData, { eventID: eventId });
    logEvent('CompleteRegistration', eventData, eventId, 'sent');
    return eventId;
  }
  
  logEvent('CompleteRegistration', eventData, null, 'error', 'fbq not available');
  return null;
};

// Track Lead event (Trial Activation)
export const trackLead = (trialData = {}) => {
  log('trackLead called');
  
  const eventData = {
    content_name: 'SmartBalloon Trial',
    content_category: '7-Day AI Trial',
    ...trialData
  };
  
  if (!hasMarketingConsent()) {
    logEvent('Lead', eventData, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  if (!initMetaPixel()) {
    logEvent('Lead', eventData, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  const eventId = generateEventId();
  
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Lead', eventData, { eventID: eventId });
    logEvent('Lead', eventData, eventId, 'sent');
    return eventId;
  }
  
  logEvent('Lead', eventData, null, 'error', 'fbq not available');
  return null;
};

// Track Login event (custom)
export const trackLogin = (loginData = {}) => {
  log('trackLogin called');
  
  const eventData = {
    content_name: 'SmartBalloon Login',
    ...loginData
  };
  
  if (!hasMarketingConsent()) {
    logEvent('Login', eventData, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  if (!initMetaPixel()) {
    logEvent('Login', eventData, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  const eventId = generateEventId();
  
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'Login', eventData, { eventID: eventId });
    logEvent('Login', eventData, eventId, 'sent');
    return eventId;
  }
  
  logEvent('Login', eventData, null, 'error', 'fbq not available');
  return null;
};

// Track Purchase event
export const trackPurchase = (purchaseData) => {
  log('trackPurchase called');
  console.log('[META DEBUG] trackPurchase() CALLED with data:', purchaseData);
  
  const eventData = {
    value: purchaseData.value || 0,
    currency: purchaseData.currency || 'EUR',
    content_name: purchaseData.plan || 'SmartBalloon Subscription',
    content_type: 'subscription',
    content_ids: [purchaseData.planId || 'smartballoon_subscription'],
    ...purchaseData
  };
  
  const consentStatus = hasMarketingConsent();
  console.log('[META DEBUG] hasMarketingConsent() returned:', consentStatus);
  
  if (!consentStatus) {
    console.log('[META DEBUG] ❌ BLOCKED: No marketing consent');
    logEvent('Purchase', eventData, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  const pixelInitialized = initMetaPixel();
  console.log('[META DEBUG] initMetaPixel() returned:', pixelInitialized);
  
  if (!pixelInitialized) {
    console.log('[META DEBUG] ❌ BLOCKED: Pixel not initialized');
    logEvent('Purchase', eventData, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  const eventId = generateEventId();
  console.log('[META DEBUG] Generated eventId:', eventId);
  
  if (typeof window !== 'undefined' && window.fbq) {
    console.log('[META DEBUG] ✅ window.fbq EXISTS, sending Purchase event');
    window.fbq('track', 'Purchase', eventData, { eventID: eventId });
    logEvent('Purchase', eventData, eventId, 'sent');
    console.log('[META DEBUG] ✅ Purchase PIXEL sent successfully');
    return eventId;
  }
  
  console.log('[META DEBUG] ❌ BLOCKED: window.fbq not available');
  logEvent('Purchase', eventData, null, 'error', 'fbq not available');
  return null;
};

// Track ViewContent event (e.g., pricing page)
export const trackViewContent = (contentData = {}) => {
  log('trackViewContent called');
  
  const eventData = {
    content_type: 'page',
    ...contentData
  };
  
  if (!hasMarketingConsent()) {
    logEvent('ViewContent', eventData, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  if (!initMetaPixel()) {
    logEvent('ViewContent', eventData, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  const eventId = generateEventId();
  
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'ViewContent', eventData, { eventID: eventId });
    logEvent('ViewContent', eventData, eventId, 'sent');
    return eventId;
  }
  
  logEvent('ViewContent', eventData, null, 'error', 'fbq not available');
  return null;
};

// Track InitiateCheckout event
export const trackInitiateCheckout = (checkoutData = {}) => {
  log('trackInitiateCheckout called');
  
  const eventData = {
    content_type: 'subscription',
    ...checkoutData
  };
  
  if (!hasMarketingConsent()) {
    logEvent('InitiateCheckout', eventData, null, 'blocked', 'No marketing consent');
    return null;
  }
  
  if (!initMetaPixel()) {
    logEvent('InitiateCheckout', eventData, null, 'blocked', 'Pixel not initialized');
    return null;
  }
  
  const eventId = generateEventId();
  
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'InitiateCheckout', eventData, { eventID: eventId });
    logEvent('InitiateCheckout', eventData, eventId, 'sent');
    return eventId;
  }
  
  logEvent('InitiateCheckout', eventData, null, 'error', 'fbq not available');
  return null;
};

// Helper to get cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Send event to server for Conversion API
export const sendServerEvent = async (eventName, eventData, eventId, userEmail = null) => {
  log('sendServerEvent called:', eventName, eventId);
  
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    
    // Get Facebook cookies for better Event Match Quality
    const fbc = getCookie('_fbc');  // Facebook Click ID
    const fbp = getCookie('_fbp');  // Facebook Browser ID
    
    if (fbc) log('Found _fbc cookie');
    if (fbp) log('Found _fbp cookie');
    
    const response = await fetch(`${API_URL}/api/meta/conversion-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_name: eventName,
        event_data: eventData,
        event_id: eventId,
        user_email: userEmail,
        event_source_url: window.location.href,
        client_user_agent: navigator.userAgent,
        fbc: fbc,  // Facebook Click ID
        fbp: fbp,  // Facebook Browser ID
      }),
    });
    
    if (response.ok) {
      log('Server event sent successfully:', eventName, eventId);
    } else {
      log('Server event failed:', response.status);
    }
  } catch (error) {
    log('Server event error:', error);
  }
};

// Combined tracking: Client Pixel + Server Conversion API
export const trackEvent = async (eventName, eventData = {}, userEmail = null) => {
  log('trackEvent called:', eventName);
  
  if (!hasMarketingConsent()) {
    log('Marketing consent not given, skipping:', eventName);
    return null;
  }
  
  const eventId = generateEventId();
  
  // Client-side tracking
  if (initMetaPixel() && typeof window !== 'undefined' && window.fbq) {
    if (['PageView', 'CompleteRegistration', 'Lead', 'Purchase', 'ViewContent', 'InitiateCheckout'].includes(eventName)) {
      window.fbq('track', eventName, eventData, { eventID: eventId });
    } else {
      window.fbq('trackCustom', eventName, eventData, { eventID: eventId });
    }
    log('Client event tracked:', eventName, eventId);
  }
  
  // Server-side tracking for critical events
  if (['CompleteRegistration', 'Lead', 'Purchase'].includes(eventName)) {
    await sendServerEvent(eventName, eventData, eventId, userEmail);
  }
  
  return eventId;
};

// Debug helper - call from browser console: window.debugMetaPixel()
if (typeof window !== 'undefined') {
  window.debugMetaPixel = () => {
    console.log('=== META PIXEL DEBUG ===');
    console.log('Pixel ID:', META_PIXEL_ID);
    console.log('Has Marketing Consent:', hasMarketingConsent());
    console.log('window.__metaPixelConsent:', window.__metaPixelConsent);
    console.log('window.__fbqInitialized:', window.__fbqInitialized);
    console.log('window.fbq exists:', !!window.fbq);
    console.log('localStorage consent:', localStorage.getItem('smartballoon-cookie-consent'));
    console.log('=========================');
    
    return {
      pixelId: META_PIXEL_ID,
      hasConsent: hasMarketingConsent(),
      fbqExists: !!window.fbq,
      initialized: !!window.__fbqInitialized
    };
  };
  
  // Also expose force init for testing
  window.forceInitMetaPixel = forceInitMetaPixel;
  window.testMetaPixelPageView = () => {
    window.__metaPixelConsent = true;
    initMetaPixel();
    return trackPageView(window.location.pathname);
  };
}

export default {
  initMetaPixel,
  forceInitMetaPixel,
  hasMarketingConsent,
  trackPageView,
  trackCompleteRegistration,
  trackLead,
  trackLogin,
  trackPurchase,
  trackViewContent,
  trackInitiateCheckout,
  trackEvent,
  sendServerEvent,
  generateEventId,
};
